import { ConfigApi, IdentityApi } from '@backstage/core-plugin-api';

type CaptureOptions = { attributes?: Record<string, unknown>; value?: number };

type CaptureInput =
  | string
  | {
      action: string;
      subject?: string;
      attributes?: Record<string, unknown>;
      context?: Record<string, unknown>;
      value?: number;
    };

interface NormalizedTelemetryEvent {
  action: string;
  subject: string;
  attributes: Record<string, unknown>;
}

// Actions the backend stores.
const PERSISTED_ACTIONS = new Set([
  'user_login',
  'page_view',
  'asset_click',
  'marketplace_click',
]);

// Actions accepted from callers ('navigate' is an alias mapped to page_view).
const INBOUND_ACTIONS = new Set([...PERSISTED_ACTIONS, 'navigate']);

// Only these attribute keys are forwarded; everything else is dropped.
const ATTRIBUTE_ALLOWLIST = new Set([
  'asset_id',
  'title',
  'type',
  'team',
  'service',
]);

// Never send these (PII / secrets), even if allowlisted elsewhere.
const FORBIDDEN_ATTRIBUTE_KEYS = new Set([
  'user_email',
  'token',
  'password',
  'authorization',
]);

/**
 * Custom analytics API that forwards allowlisted telemetry events to the backend.
 * Fire-and-forget: never throws to callers.
 */
export class CustomAnalyticsApi {
  private readonly identityApi: IdentityApi;
  private readonly baseUrl: string;

  constructor(configApi: ConfigApi, identityApi: IdentityApi) {
    this.identityApi = identityApi;
    this.baseUrl = configApi.getString('backend.baseUrl');
  }

  /**
   * Synchronous, fire-and-forget event capture. Accepts object or positional overload.
   */
  captureEvent(
    eventOrAction: CaptureInput,
    subject?: string,
    options?: CaptureOptions,
  ): void {
    const normalized = this.normalizeEvent(eventOrAction, subject, options);
    // Ignore malformed or non-tracked actions.
    if (!normalized || !this.shouldPersist(normalized.action)) {
      return;
    }

    const persistedAction = this.mapAction(normalized.action);
    const attributes = this.sanitizeAttributes(normalized.attributes);

    void this.sendAnalyticsEvent({
      action: persistedAction,
      subject: normalized.subject,
      attributes,
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.error('Error tracking analytics event:', err);
    });
  }

  // Collapse the string and object call styles into one shape.
  private normalizeEvent(
    eventOrAction: CaptureInput,
    subject?: string,
    options?: CaptureOptions,
  ): NormalizedTelemetryEvent | null {
    if (typeof eventOrAction === 'string') {
      return {
        action: eventOrAction,
        subject: subject ?? '',
        attributes: options?.attributes ?? {},
      };
    }

    if (eventOrAction && typeof eventOrAction === 'object' && eventOrAction.action) {
      return {
        action: eventOrAction.action,
        subject: eventOrAction.subject ?? '',
        attributes: eventOrAction.attributes ?? {},
      };
    }

    return null;
  }

  private shouldPersist(action: string): boolean {
    return INBOUND_ACTIONS.has(action);
  }

  // Router-level 'navigate' events are stored as page_view.
  private mapAction(action: string): string {
    if (action === 'navigate') {
      return 'page_view';
    }
    return action;
  }

  // Keep only allowlisted, non-sensitive, defined attributes.
  private sanitizeAttributes(
    attributes: Record<string, unknown>,
  ): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(attributes)) {
      if (!ATTRIBUTE_ALLOWLIST.has(key)) {
        continue;
      }
      if (FORBIDDEN_ATTRIBUTE_KEYS.has(key)) {
        continue;
      }
      if (/email/i.test(key)) {
        continue;
      }
      if (value === undefined) {
        continue;
      }
      sanitized[key] = value;
    }

    return sanitized;
  }

  // POST the event to the backend with the caller's bearer token.
  private async sendAnalyticsEvent(
    normalized: NormalizedTelemetryEvent,
  ): Promise<void> {
    try {
      const credentials = await this.identityApi.getCredentials();
      if (!credentials.token) {
        // eslint-disable-next-line no-console
        console.warn(
          'No authentication token available for analytics tracking',
        );
        return;
      }

      const metric = {
        action: normalized.action,
        subject: normalized.subject,
        attributes: normalized.attributes,
        timestamp: new Date().toISOString(),
      };

      const response = await fetch(
        `${this.baseUrl}/api/custom-api/user-metrics`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${credentials.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(metric),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to track analytics event: ${response.statusText}`,
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error tracking analytics event:', error);
    }
  }
}
